import { ReactElement, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TextField } from '../../../components/TextField';
import { Button } from '../../../components/Button/Button';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { Routes } from '../../../enums/Routes';
import { useGetTenantsQuery } from '../../../hooks/queries/useGetTenantsQuery';
import { useUpdateTenantConfigMutation } from '../../../hooks/mutations/useUpdateTenantConfigMutation';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import classes from './EditSmsQuotaPage.module.css';

const PAGE_TITLE = 'Endre SMS-kvote';
const QUOTA_LABEL = 'SMS-kvote';
const QUOTA_PLACEHOLDER = 'Antall SMS-er, eller la stå tom for deaktivert';
const RESET_DATE_LABEL = 'Tilbakestillingsdato (valgfri)';
const SAVE_BUTTON_TEXT = 'Lagre endringer';
const DEACTIVATE_TEXT = 'La feltet stå tomt for å deaktivere SMS-kvoten';
const ERROR_MESSAGE = 'Kunne ikke laste kundedata. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Kunde ikke funnet.';
const BACK_LINK_LABEL = 'Tilbake til kundedetaljer';

export const EditSmsQuotaPage = (): ReactElement => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetTenantsQuery();
  const updateTenantConfigMutation = useUpdateTenantConfigMutation();

  const tenant = data?.tenants.find((t) => t.id === Number(tenantId));

  const [quota, setQuota] = useState<string>('');
  const [resetDate, setResetDate] = useState<string>('');

  useEffect(() => {
    if (tenant) {
      setQuota(
        tenant.smsQuota && tenant.smsQuota > 0 ? tenant.smsQuota.toString() : ''
      );
      setResetDate(
        tenant.smsQuotaResetAt
          ? new Date(tenant.smsQuotaResetAt).toISOString().split('T')[0]
          : ''
      );
    }
  }, [tenant]);

  const backLink = Routes.SystemAdminTenantDetail.replace(':tenantId', tenantId || '');

  const handleSave = () => {
    if (!tenantId) return;

    const quotaValue = quota.trim() === '' ? null : Number(quota);
    const resetDateValue = resetDate.trim() === '' ? null : new Date(resetDate).toISOString();

    updateTenantConfigMutation.mutate(
      {
        tenantId: Number(tenantId),
        payload: {
          smsQuota: quotaValue,
          smsQuotaResetAt: resetDateValue,
        },
      },
      {
        onSuccess: () => {
          toast.success('SMS-kvote oppdatert');
          navigate(backLink);
        },
        onError: () => {
          toast.error('Kunne ikke oppdatere SMS-kvote. Prøv igjen senere.');
        },
      }
    );
  };

  const handleQuotaChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      setQuota(value);
    }
  };

  if (isLoading) {
    return <GhostLoader />;
  }

  if (error) {
    return (
      <Alert variant="danger" size="md">
        <Paragraph>{ERROR_MESSAGE}</Paragraph>
      </Alert>
    );
  }

  if (!tenant) {
    return (
      <Alert variant="warning" size="md">
        <Paragraph>{NOT_FOUND_MESSAGE}</Paragraph>
      </Alert>
    );
  }

  return (
    <FocusPageLayout
      title={PAGE_TITLE}
      subtitle={`Oppdater SMS-kvoten for ${tenant.name}`}
      backLink={backLink}
      backLinkLabel={BACK_LINK_LABEL}
    >
      <div className={classes.form}>
        <div className={classes.section}>
          <TextField
            label={QUOTA_LABEL}
            type="text"
            name="quota"
            inputMode="numeric"
            value={quota}
            onChange={(e) => handleQuotaChange(e.target.value)}
            placeholder={QUOTA_PLACEHOLDER}
          />
          <Paragraph className={classes.helperText}>
            {DEACTIVATE_TEXT}
          </Paragraph>
        </div>

        <div className={classes.section}>
          <TextField
            label={RESET_DATE_LABEL}
            type="date"
            name="resetDate"
            value={resetDate}
            onChange={(e) => setResetDate(e.target.value)}
          />
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            onClick={() => navigate(backLink)}
            disabled={updateTenantConfigMutation.isPending}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateTenantConfigMutation.isPending}
          >
            {updateTenantConfigMutation.isPending ? 'Lagrer...' : SAVE_BUTTON_TEXT}
          </Button>
        </div>
      </div>
    </FocusPageLayout>
  );
};
